import { Box, FormHelperText, IconButton } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"

import { useExpenseAccountOptions } from "../../hooks/useExpenseAccountOptions"
import { type InvoiceFormValues, getExpenseLinesSumMismatchMessage } from "../../schemas/invoiceForm"

import { Button } from "@/components/common/Button"
import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"

const EMPTY_EXPENSE_LINE: InvoiceFormValues["expenseLines"][number] = { expenseAccountId: "", amount: null }

interface ExpenseLinesSectionProps {
  disabled?: boolean
}

export const ExpenseLinesSection: FC<ExpenseLinesSectionProps> = ({ disabled }) => {
  const { control } = useFormContext<InvoiceFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: "expenseLines" })

  const totalAmount = useWatch({ control, name: "totalAmount" })
  const expenseLines = useWatch({ control, name: "expenseLines" })

  // AK-6337: the superRefine issue anchored at `expenseLines` (array root) never re-syncs into
  // formState.errors under mode: "onChange", so the message is derived here instead.
  const sumMismatchMessage = useMemo(
    () => getExpenseLinesSumMismatchMessage(totalAmount, expenseLines),
    [totalAmount, expenseLines]
  )

  const { expenseAccounts } = useExpenseAccountOptions()

  const accountOptions = useMemo(
    () => expenseAccounts.map((account) => ({ value: account.id, label: account.label })),
    [expenseAccounts]
  )

  const handleAddLine = () => {
    append({ ...EMPTY_EXPENSE_LINE })
  }

  const canRemoveLine = fields.length > 1

  return (
    <Box className='flex flex-col gap-2'>
      {fields.map((field, index) => (
        <Box key={field.id} className='flex items-start gap-2'>
          <Box className='flex-1'>
            <FormAutocomplete
              control={control}
              name={`expenseLines.${index}.expenseAccountId`}
              label='Expense Account'
              placeholder='Select...'
              options={accountOptions}
              required
              disabled={disabled}
              data-testid={`invoice.form.expense-line-account-field-${index}`}
            />
          </Box>
          <Box className='w-40'>
            <FormNumericField
              control={control}
              name={`expenseLines.${index}.amount`}
              label='Amount'
              required
              prefix='$'
              decimalScale={2}
              allowNegative={false}
              disabled={disabled}
              data-testid={`invoice.form.expense-line-amount-field-${index}`}
            />
          </Box>
          {canRemoveLine && !disabled && (
            <IconButton
              size='small'
              onClick={() => remove(index)}
              data-testid={`invoice.form.expense-line-remove-button-${index}`}
              className='mt-7'
            >
              <DeleteOutlined fontSize='small' />
            </IconButton>
          )}
        </Box>
      ))}

      {!disabled && sumMismatchMessage && (
        <FormHelperText error data-testid='invoice.form.expense-lines-sum-error'>
          {sumMismatchMessage}
        </FormHelperText>
      )}

      {!disabled && (
        <Box className='flex justify-end'>
          <Button
            appearance='text'
            startIcon={<AddOutlined />}
            onClick={handleAddLine}
            data-testid='invoice.form.add-expense-line-button'
          >
            Add Another Expense Account
          </Button>
        </Box>
      )}
    </Box>
  )
}
